import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { API_URLS } from '@pinnakl/core/web-services';
import { from, switchMap, throwError } from 'rxjs';
import { CrmWorkspacesStore } from './crm-workspaces.store';

export const CRM_WORKSPACE_HEADER = 'x-workspace-id';

const CORE_API_URL_MARKERS = ['apicore', 'localhost:51490', 'localhost:53589'];

export const crmWorkspaceHeaderInterceptor: HttpInterceptorFn = (request, next) => {
  const workspacesStore = inject(CrmWorkspacesStore);
  const workspacesEndpoint = inject(API_URLS).crmWorkspaces;
  const isApiCoreService = CORE_API_URL_MARKERS.some(marker => request.url.includes(marker));
  // The workspace list itself must stay excluded: it is fetched through this same interceptor, so
  // making it wait on whenWorkspaceResolved() would deadlock it against its own in-flight promise.
  if (!isApiCoreService || request.url.includes(workspacesEndpoint)) {
    return next(request);
  }

  return from(workspacesStore.whenWorkspaceResolved()).pipe(
    switchMap(activeWorkspaceId => {
      if (activeWorkspaceId !== null) {
        return next(request.clone({ setHeaders: { [CRM_WORKSPACE_HEADER]: String(activeWorkspaceId) } }));
      }
      // Boot window: /v4/users has to go out to authenticate the user, and the workspace list can
      // only be fetched once it has. Nothing workspace-scoped runs before that first fetch starts,
      // so these requests are genuinely unscoped rather than mis-scoped.
      if (!workspacesStore.workspacesLoadAttempted()) {
        return next(request);
      }
      // Fail closed. The list was fetched and still yields no usable workspace, so sending this
      // request would silently read the BE default workspace — potentially another client's data.
      return throwError(
        () => new Error('Workspace could not be resolved, so the request was blocked. Please reload and try again.'),
      );
    }),
  );
};
